import {
  useState,
  useEffect,
  useMemo,
  JSXElementConstructor,
  Key,
  ReactElement,
} from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

// Material Dashboard 2 PRO React TS exampless
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 PRO React TS themes
import theme from "assets/theme";

// Material Dashboard 2 PRO React TS Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 PRO React TS routes
import routes from "routes";

// Material Dashboard 2 PRO React TS contexts
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
  setMessage,
  setUser,
  setIsAuthencated,
  setNotificationMessage,
} from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import { siteConfig } from "types/siteConfig";

import RoutePrivate from "layouts/RoutePrivate";
import SignInBasic from "layouts/authentication/sign-in/basic";
import Cover from "layouts/authentication/sign-up/cover";
import NewEvento from "./layouts/pages/cadatros/eventos/new-evento";
import LojaProdutoForm from "layouts/pages/loja/produto/forms";
import LojaPedidoDetalhe from "layouts/pages/loja/pedidos/detalhe";
import LojaPedidoFaturamento from "layouts/pages/loja/pedidos/faturamento";
import LojaPedidoSeparacao from "layouts/pages/loja/pedidos/separacao";
import LojaPedidoExpedicao from "layouts/pages/loja/pedidos/expedicao";
import LojaPedidoRetirada from "layouts/pages/loja/pedidos/retirada";
import LojaPagamentoDetalhe from "layouts/pages/loja/pagamentos/detalhe";

import { checkAccessToken } from "services/users";
import { refreshUserAccess } from "services/access";
import { getAllMessengers } from "services/messenger";
import { filterRoutesByAccess } from "helpers/filterRoutesByAccess";
import { accessFromUser } from "helpers/permissions";

import { keyLocalStorage } from "helpers/constants";
import SemPermissaoPage from "layouts/pages/sem-permissao";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();

  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
    message,
    user,
    isAuthencated,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const accessSnapshot = useMemo(() => accessFromUser(user), [user]);

  const visibleRoutes = useMemo(
    () => filterRoutesByAccess(routes as any[], accessSnapshot),
    [accessSnapshot]
  );

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);

  useEffect(() => {
    (async () => {
      if (pathname === "/authentication/sign-in/basic" || pathname === "/") {
        document.body.setAttribute("dir", direction);
        return;
      }

      const tokenCheck = await checkAccessToken();
      if (tokenCheck.expiredToken) {
        localStorage.removeItem(keyLocalStorage.USER_STORAGE);
        setUser(dispatch, null);
        setIsAuthencated(dispatch, false);
      } else if (isAuthencated) {
        const updated = await refreshUserAccess();
        if (updated) setUser(dispatch, updated);
      }

      document.body.setAttribute("dir", direction);
    })();
  }, [direction, pathname, isAuthencated, dispatch]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    const refreshMessengerNotifications = async () => {
      const raw = localStorage.getItem(keyLocalStorage.USER_STORAGE);
      if (!raw) return;

      try {
        const { data, status } = await getAllMessengers();
        if (status === 200) {
          setNotificationMessage(
            dispatch,
            data.filter((item) => item.status === false).length
          );
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.warn("[messenger] Falha ao carregar notificacoes", error);
        }
      }
    };

    refreshMessengerNotifications();
    const interval = setInterval(refreshMessengerNotifications, 60000 * 10); // Consulta a cada 10 minutos

    return () => clearInterval(interval);
  }, [dispatch]);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
        private: boolean;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          if (route.private) {
            return (
              <Route key={route.key} element={<RoutePrivate />}>
                <Route path={route.route} element={route.component} />
              </Route>
            );
          }
          // Routes public
          return (
            <Route
              path={route.route}
              element={route.component}
              key={route.key}
            />
          );
        }

        return null;
      }
    );

  const handlerOpenAlert = () => {
    setMessage(dispatch, { open: false, severity: "info", text: "" });
  };

  {
    /*const configsButton = (
    <>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        width="3.25rem"
        height="3.25rem"
        bgColor="white"
        shadow="sm"
        borderRadius="50%"
        position="fixed"
        right="2rem"
        bottom="2rem"
        zIndex={99}
        color="dark"
        sx={{ cursor: "pointer" }}
        onClick={handleConfiguratorOpen}
      >
        <Icon fontSize="small" color="inherit">
          settings
        </Icon>
      </MDBox>

      <Snackbar
        open={message.open}
        onClose={handlerOpenAlert}
        autoHideDuration={4000}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Alert severity={message.severity} variant="filled" sx={{ width: "100%" }}>
          {message.text}
        </Alert>
      </Snackbar>
    </>
  );*/
  }

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {/* Alerta de message */}
      <Snackbar
        open={message.open}
        onClose={handlerOpenAlert}
        autoHideDuration={4000}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <Alert
          severity={message.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message.text}
        </Alert>
      </Snackbar>

      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={
              (transparentSidenav && !darkMode) || whiteSidenav
                ? brandDark
                : brandWhite
            }
            brandName={siteConfig.name}
            routes={visibleRoutes as typeof routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <Configurator />
          {/*{configsButton}*/}
        </>
      )}
      {layout === "vr" && <Configurator />}

      <Routes>
        {getRoutes(routes)}
        <Route path="/authentication/sign-in/basic" element={<SignInBasic />} />
        <Route path="/authentication/sign-up/cover" element={<Cover />} />
        <Route element={<RoutePrivate />}>
          <Route path="/pages/sem-permissao" element={<SemPermissaoPage />} />
          <Route path="/loja/produtos/create" element={<LojaProdutoForm />} />
          <Route
            path="/loja/produtos/editar/:produtoId"
            element={<LojaProdutoForm />}
          />
          <Route
            path="/loja/pedidos/:pedidoId/faturamento"
            element={<LojaPedidoFaturamento />}
          />
          <Route
            path="/loja/pedidos/:pedidoId/separacao"
            element={<LojaPedidoSeparacao />}
          />
          <Route
            path="/loja/pedidos/:pedidoId/expedicao"
            element={<LojaPedidoExpedicao />}
          />
          <Route
            path="/loja/pedidos/:pedidoId/retirada"
            element={<LojaPedidoRetirada />}
          />
          <Route
            path="/loja/pedidos/:pedidoId"
            element={<LojaPedidoDetalhe />}
          />
          <Route
            path="/loja/pagamentos/:pagamentoId"
            element={<LojaPagamentoDetalhe />}
          />
          <Route
            path="/layouts/pages/cadastros/eventos/:eventoSlug"
            element={<NewEvento />}
          />
        </Route>
        <Route
          path="/pages/eventos"
          element={<Navigate to="/layouts/pages/cadastros/eventos" replace />}
        />
        <Route
          path="*"
          element={<Navigate to="/pages/relatorios/estatisticas" />}
        />
      </Routes>
    </ThemeProvider>
  );
}
